import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Theme, Typography } from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";

import { VACATION_BID_TEST_ID } from "../testIds";
import { ICsvIssue } from "src/utils/csvFile";

export interface IImportCsvDialogProps {
  open: boolean;
  title: string;
  /** The chosen file's name, so the admin can see they picked the one they meant to. */
  fileName: string;
  /** One statement per line describing what confirming will do. */
  summary: string[];
  issues: ICsvIssue[];
  /** Set when the file could not be used at all; the confirm button is then hidden. */
  fatal?: string;
  confirmLabel: string;
  confirmDisabled?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

const ImportCsvDialogStyles = (theme: Theme): any => ({
  paper: {
    backgroundColor: theme.palette.background.paper,
    minWidth: 540,
    maxWidth: 720,
    "& .MuiDialogTitle-root": {
      ...theme.typography.subtitle2,
      fontSize: 16,
      padding: "12px 16px",
    },
    "& .MuiDialogContent-root": {
      padding: "0 16px 12px",
    },
    "& .import_file_name": {
      color: theme.custom.forms.labels,
      wordBreak: "break-all",
      marginBottom: "8px",
    },
    "& .import_summary_line": {
      display: "block",
    },
    "& .import_fatal": {
      display: "flex",
      alignItems: "center",
      gap: "6px",
      color: theme.palette.secondary.main,
    },
    "& .import_issues_heading": {
      display: "flex",
      alignItems: "center",
      gap: "6px",
      marginTop: "12px",
      color: theme.palette.secondary.main,
    },
    "& .import_issues": {
      maxHeight: 220,
      overflowY: "auto",
      marginTop: "4px",
      paddingLeft: "18px",
      "& li": {
        ...theme.typography.caption,
        color: theme.custom.forms.labels,
      },
    },
  },
});

/**
 * Confirms a CSV import before it touches anything.
 *
 * Importing replaces what is on screen, so the admin is always shown the row count and every line
 * that could not be read before it happens.
 */
export const ImportCsvDialog = (props: IImportCsvDialogProps): JSX.Element => {
  const { open, title, fileName, summary, issues, fatal, confirmLabel, confirmDisabled, onConfirm, onClose } = props;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      data-testid={VACATION_BID_TEST_ID.importCsvDialog}
      PaperProps={{ sx: (theme: Theme) => ImportCsvDialogStyles(theme).paper }}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="caption" className="import_file_name">
          {fileName}
        </Typography>

        {fatal ? (
          <Typography variant="body2" className="import_fatal">
            <WarningIcon fontSize="small" />
            {fatal}
          </Typography>
        ) : (
          summary.map((line) => (
            <Typography key={line} variant="body2" className="import_summary_line">
              {line}
            </Typography>
          ))
        )}

        {!!issues.length && (
          <>
            <Typography variant="body2" className="import_issues_heading">
              <WarningIcon fontSize="small" />
              {`${issues.length} line${issues.length > 1 ? "s" : ""} could not be read and will be skipped`}
            </Typography>
            <ul className="import_issues" data-testid={VACATION_BID_TEST_ID.importCsvIssues}>
              {issues.map((issue) => (
                <li key={`${issue.line}-${issue.message}`}>{`Line ${issue.line}: ${issue.message}`}</li>
              ))}
            </ul>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          size="small"
          variant="contained"
          color="secondary"
          onClick={onClose}
          data-testid={VACATION_BID_TEST_ID.importCsvCancelButton}
        >
          {fatal ? "Close" : "Cancel"}
        </Button>
        {!fatal && (
          <Button
            size="small"
            variant="contained"
            color="primary"
            disabled={confirmDisabled}
            onClick={onConfirm}
            data-testid={VACATION_BID_TEST_ID.importCsvConfirmButton}
          >
            {confirmLabel}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportCsvDialog;
